import { getDashboardLeaguesControllerListQueryKey } from "@/api/__generated__/dashboard-leagues/dashboard-leagues";
import { MessageKey } from "@/i18n";
import {
  LeagueAthleteExportColumn,
  LeagueAthleteExportColumnValues,
  LeagueAthletesTableColumn,
  LeagueGeoScope,
  LeagueManagementMemberTableColumn,
  LeagueMemberRoleName,
  LeagueStatus,
  LeagueTeamExportColumn,
  LeagueTeamExportColumnValues,
  LeagueTeamsTableColumn,
  LeagueTourType,
} from "@/types";

export const LEAGUES_INVALIDATE_KEYS = [getDashboardLeaguesControllerListQueryKey()];

export const LEAGUE_ATHLETES_TABLE_COLUMN_LABELS: {
  [key in LeagueAthletesTableColumn]: MessageKey;
} = {
  id: "general.columns.id",
  user: "league.columns.athletesTable.user",
  age: "general.columns.age",
  sports: "league.columns.athletesTable.sports",
  clubs: "league.columns.athletesTable.clubs",
  tournaments: "league.columns.athletesTable.tournaments",
};

export const LEAGUE_TEAMS_TABLE_COLUMN_LABELS: {
  [key in LeagueTeamsTableColumn]: MessageKey;
} = {
  id: "general.columns.id",
  title: "team.columns.teamName",
  club: "general.columns.club",
  sport: "team.columns.sports",
  contact: "team.columns.contact",
  members: "team.columns.members",
  tournaments: "league.columns.teamsTable.tournaments",
};

export const LEAGUE_ATHLETE_EXPORT_COLUMNS: readonly LeagueAthleteExportColumn[] =
  LeagueAthleteExportColumnValues;

export const LEAGUE_ATHLETE_EXPORT_COLUMN_LABELS: {
  [key in LeagueAthleteExportColumn]: MessageKey;
} = {
  name: "general.columns.name",
  surname: "general.columns.surname",
  email: "general.columns.email",
  tournatedTag: "league.athlete.export.columns.tournatedTag",
  nation: "league.athlete.export.columns.nation",
  birthDate: "league.athlete.export.columns.birthDate",
  sports: "league.columns.athletesTable.sports",
  clubs: "league.columns.athletesTable.clubs",
  tournamentsCount: "league.columns.athletesTable.tournaments",
};

export const LEAGUE_TEAM_EXPORT_COLUMNS: readonly LeagueTeamExportColumn[] =
  LeagueTeamExportColumnValues;

export const LEAGUE_TEAM_EXPORT_COLUMN_LABELS: {
  [key in LeagueTeamExportColumn]: MessageKey;
} = {
  title: "general.columns.title",
  country: "general.columns.country",
  email: "general.columns.email",
  phone: "general.columns.phone",
  sport: "league.columns.sport",
  clubName: "general.columns.club",
  membersCount: "league.team.export.columns.membersCount",
  tournamentsCount: "league.columns.teamsTable.tournaments",
};

export const LEAGUE_STATUS_LABELS: { [key in LeagueStatus]: MessageKey } = {
  active: "league.status.active",
  inactive: "league.status.inactive",
};

export const LEAGUE_GEO_SCOPE_LABELS: { [key in LeagueGeoScope]: MessageKey } = {
  national: "league.type.national",
  international: "league.type.international",
  local: "league.type.local",
};

export const LEAGUE_TOUR_TYPE_LABELS: { [key in LeagueTourType]: MessageKey } = {
  tour: "league.enums.tourType.tour",
  circuit: "league.enums.tourType.circuit",
  league: "league.enums.tourType.league",
  series: "league.enums.tourType.series",
  festival: "league.enums.tourType.festival",
  other: "league.enums.tourType.other",
};

export const LEAGUE_MEMBER_ROLE_LABELS: { [key in LeagueMemberRoleName]: MessageKey } = {
  organizer: "league.enums.memberRole.organizer",
  manager: "league.enums.memberRole.manager",
  referee: "league.enums.memberRole.referee",
};

/**
 * Role → design-system `Badge` colour for league-member role badges, shared by
 * the League team table and the federation management-team accordion so both
 * render identical chips (organizer = red, manager = green, referee = blue). The
 * legacy `handleLeagueManagerRoleColor` returned the old MUI `Chip` palette,
 * which are not valid `Badge` colours — the same intent is expressed here with
 * the modern `Badge` palette instead.
 */
export const LEAGUE_MEMBER_ROLE_BADGE_COLORS: {
  [key in LeagueMemberRoleName]: "danger" | "positive" | "blue";
} = {
  organizer: "danger",
  manager: "positive",
  referee: "blue",
};

export const LEAGUE_MANAGEMENT_MEMBER_TABLE_COLUMN_LABELS: {
  [key in LeagueManagementMemberTableColumn]: MessageKey;
} = {
  userId: "general.columns.id",
  user: "league.team.columns.user",
  contact: "general.columns.contactDetails",
  roles: "league.team.columns.roles",
  tournaments: "league.columns.athletesTable.tournaments",
  actions: "general.columns.actions",
};
